// @flow

import {convertLocationParamsFoldersDocumentsToFetchParams} from 'app/documents/actions';
import type {
  DocumentType,
  DashboardFetchParamsType,
  DashboardLocationParamsType,
  DashboardFoldersFetchParamsType,
} from 'app/documents/types';
import type {
  FolderType,
  RawFolderType,
  FolderRecurrentParentType,
  RawRecurrentFolderType,
} from 'app/folders/types';
import type {PermissionCodeType} from 'app/general/types';
import {
  DOCUMENTS_API_URL,
  FOLDERS_API_URL,
  FOLDERS_HASHID_API_URL,
} from 'app/urls';
import * as urls from 'app/urls';
import {gt, ngt} from 'app/utils/i18n';
import {type ResponseType, type RelatedResponseType} from 'app/utils/redux_v2';
import * as request from 'app/utils/request';
import {showNotification} from 'app/toasts/slice';
import type {BasicTeamType} from 'app/teams/types';

import {limitRequests} from 'app/utils/redux';
import {get} from 'app/utils/request';
import {singletonPromise} from 'app/utils/promises';
import type {RelatedResponseTypeSingle} from 'app/utils/redux_v2';

import {
  foldersFetcher,
  createDocumentFetcher,
  createFolderFetcher,
} from 'app/folders/fetchers';

export const REQUEST_FOLDERS_DASHBOARDS_LIST =
  'REQUEST_FOLDERS_DASHBOARDS_LIST';
export const RECEIVE_FOLDERS_DASHBOARDS_LIST =
  'RECEIVE_FOLDERS_DASHBOARDS_LIST';

export type FolderActionType =
  | {
      type: 'REQUEST_FOLDERS_DASHBOARDS_LIST',
      currentFilters: DashboardLocationParamsType,
    }
  | {
      type: 'RECEIVE_FOLDERS_DASHBOARDS_LIST',
      query: DashboardFetchParamsType,
      response: RelatedResponseType<
        RawFolderType,
        {type: 'basic_team', id: number}
      >,
    };

export const requestFoldersDashboardsList = (
  currentFilters: DashboardLocationParamsType
) => ({
  type: REQUEST_FOLDERS_DASHBOARDS_LIST,
  currentFilters: {...currentFilters},
});

export const receiveFoldersDashboardsList = (
  query: DashboardFoldersFetchParamsType,
  response: RelatedResponseType<RawFolderType, BasicTeamType>
) => ({
  type: RECEIVE_FOLDERS_DASHBOARDS_LIST,
  query,
  response,
});

/* fetch functions */

export const fetchFoldersForDashboard = limitRequests(
  (locationParams: DashboardLocationParamsType) => (dispatch: SimpleDispatch) => {
    dispatch(requestFoldersDashboardsList(locationParams));
    const fetchParams =
      convertLocationParamsFoldersDocumentsToFetchParams(locationParams);
    return request
      .get(FOLDERS_API_URL, fetchParams)
      .then(
        (response: RelatedResponseType<RawFolderType, BasicTeamType>) =>
          dispatch(receiveFoldersDashboardsList(fetchParams, response)),
        request.createErrorHandler(gt('Unable to load the folders'), dispatch)
      );
  }
);

export const fetchFoldersForInfinite = limitRequests(
  (params: DashboardLocationParamsType) => (dispatch: SimpleDispatch) => {
    dispatch(requestFoldersDashboardsList(params));
    return request
      .get(FOLDERS_API_URL, params)
      .then(
        (response: RelatedResponseType<RawFolderType, BasicTeamType>) =>
          dispatch(receiveFoldersDashboardsList(params, response)),
        request.createErrorHandler(gt('Unable to load the folders'), dispatch)
      );
  }
);

type FetchFolderProps = {
  parent?: string,
  teamId?: number,
  documentURL?: string,
  permission?: PermissionCodeType,
  search?: string,
};
export const fetchFolders = ({
  parent,
  teamId,
  documentURL,
  permission,
  search,
}: FetchFolderProps) => {
  return foldersFetcher({
    parent,
    team_id: teamId,
    document_url: documentURL,
    permission,
    search,
  }).then(
    (
      response: RelatedResponseType<RawFolderType, BasicTeamType>
    ): ResponseType<FolderType> => {
      const related = response.related;
      const results = response.results.map((item: RawFolderType) => ({
        ...item,
        teams: item.teams.map((team) => related[team.type][team.id]),
      }));
      return {
        ...response,
        results: results,
      };
    }
  );
};

export const fetchDocuments = (folder: FolderType) => {
  return request
    .get(DOCUMENTS_API_URL, {folder: folder.hashid})
    .then((response: ResponseType<DocumentType>) => response.results);
};

/* mutations */
type CreateProps = {|
  name: string,
  teamURLs: Array<string>,
  parent?: FolderType,
|};
export const createFolder =
  ({name, teamURLs, parent}: CreateProps) =>
  (dispatch: SimpleDispatch) =>
    createFolderFetcher({name, teamURLs, parent}).then(
      (response) => {
        dispatch(showNotification(gt('Folder %s created.', name)));
        return response;
      },
      request.createErrorHandler(gt('Unable to create the folder'), dispatch, {
        ...request.showForbiddenMessageHandler(true),
      })
    );

type CreateDocProps = {|
  document_content_type: 'procedure' | 'policy' | 'proceduregroup',
  object_id: number,
  folder: FolderType,
|};

export const createDocument =
  ({document_content_type, object_id, folder}: CreateDocProps) =>
  (dispatch: SimpleDispatch) =>
    createDocumentFetcher({
      folder: folder.url,
      document_content_type,
      object_id,
    }).then(
      () => dispatch(showNotification(gt('Added to the folder'))),
      request.createErrorHandler(gt('Unable to add to folder'), dispatch, {
        ...request.showForbiddenMessageHandler(true),
        ...request.showNonFieldErrorMessageHandler(
          true,
          'Unable to add to folder.'
        ),
      })
    );

export const deleteFolder =
  (folder: FolderType) => (dispatch: SimpleDispatch) =>
    request
      .del(folder.url)
      .then(
        () =>
          dispatch(
            showNotification(gt('Folder %s has been deleted.', folder.name))
          ),
        request.createErrorHandler(
          gt('Unable to delete the folder'),
          dispatch,
          request.showForbiddenMessageHandler(true)
        )
      );

export const deleteDocument =
  (document: DocumentType) => (dispatch: SimpleDispatch) =>
    request
      .del(document.url)
      .then(
        () =>
          dispatch(
            showNotification(
              gt('Document %s has been removed from a folder.', document.name)
            )
          ),
        request.createErrorHandler(
          gt('Unable to remove the document from a folder'),
          dispatch,
          request.showForbiddenMessageHandler(true)
        )
      );

type UpdateProps = {|
  name: string,
  folder: FolderType,
|};

export const updateFolder = ({name, folder}: UpdateProps) =>
  request.patchJson(folder.url, {name});

export const addTeams =
  (folder: FolderType, teamURLs: Array<string>) => (dispatch: FuncDispatch) =>
    request
      .postJson(
        urls.TEAM_FOLDERS_API_URL,
        teamURLs.map((team) => ({
          folder: folder.url,
          team,
        }))
      )
      .then(
        () =>
          dispatch(
            showNotification(ngt('Added team', 'Added teams', teamURLs.length))
          ),
        request.createErrorHandler(gt('Could not add teams.'), dispatch)
      );

export const removeTeam =
  (membership_url: string) => (dispatch: FuncDispatch) =>
    request.del(membership_url).then(
      () => dispatch(showNotification(gt('Removed team'))),
      request.createErrorHandler(gt('Could not remove team'), dispatch, {
        ...request.showNonFieldErrorMessageHandler(
          true,
          'Could not remove team'
        ),
      })
    );

/* folder detail */

export const RECEIVE_FOLDER_DETAILS = 'RECEIVE_FOLDER_DETAILS';

type ReceiveFolderDetailsType = {
  type: 'RECEIVE_FOLDER_DETAILS',
  item: RawRecurrentFolderType,
};

export const receiveFolderDetails = (
  item: RawRecurrentFolderType
): ReceiveFolderDetailsType => ({
  type: RECEIVE_FOLDER_DETAILS,
  item,
});

export const fetchFolderByHashid =
  (hashid: string) => (dispatch: SimpleDispatch) =>
    get(`${FOLDERS_HASHID_API_URL}${hashid}/`).then((response) =>
      dispatch(receiveFolderDetails(response))
    );

export const fetchFolderDetails = singletonPromise((hashid: string) =>
  get(`${FOLDERS_HASHID_API_URL}${hashid}/`).then(
    (
      folder: RelatedResponseTypeSingle<RawRecurrentFolderType, BasicTeamType>
    ): FolderRecurrentParentType => {
      //debugger;
      const {related, ...other} = folder;
      return {
        ...other,
        teams: folder.teams.map((team) => related[team.type][team.id]),
      };
    }
  )
);
